import React, { useState, useEffect, useRef } from "react";
import CinematicProductLayout, {
  CinematicHero,
  StackingSection,
  SpotlightCard,
  VelocityScroll,
  MasonryGrid,
  CinematicText,
  CinematicCardStack
} from "./shared/CinematicProductLayout";
import { CheckCircle2, FileCheck2, Activity, Zap, ShieldCheck, Phone, MessageCircle, FileDown, Layers, Crown, Cpu, Workflow } from "lucide-react";
import { motion } from "motion/react";

const MEDOS = () => {
  const logoUrl = process.env.PUBLIC_URL + "/FOZZIL MEDOS LOGO.png";
  const heroBgUrl = process.env.PUBLIC_URL + "/MEDOS Sales billing.png";
  const clientLogoUrl = process.env.PUBLIC_URL + "/jeeva health care logo.png";
  const sampleUrl = process.env.PUBLIC_URL + "/fozzil medos sample.png";

  // Form states
  const [demoName, setDemoName] = useState("");
  const [demoBranches, setDemoBranches] = useState("1-2 Branches");
  const [plan, setPlan] = useState("Yearly Plan");
  const [includeHardware, setIncludeHardware] = useState(false);
  const [requirements, setRequirements] = useState("");
  const [demoContactEmail, setDemoContactEmail] = useState("");
  const [demoContactPhone, setDemoContactPhone] = useState("");
  const [proposalEmail, setProposalEmail] = useState("");
  const [proposalPhone, setProposalPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mode, setMode] = useState(null); // 'demo' | 'proposal' | null
  const [step, setStep] = useState(0);
  const formsRef = useRef(null);

  const scrollToForms = () => {
    formsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const resetFlow = () => {
    setMode(null);
    setStep(0);
    setSubmitStatus(null);
  };
  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => Math.max(0, s - 1));

  const submitData = async (subject, data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const backendUrl = (process.env.REACT_APP_API_URL || process.env.REACT_APP_FPAY_API_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev').replace(/\/+$/, '');
      const response = await fetch(`${backendUrl}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject, data }),
      });
      const result = await response.json();
      if (response.ok && result.success) setSubmitStatus('success');
      else throw new Error(result.error);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDemo = () => {
    submitData("FOZZIL MEDOS - Demo Request", { organizationName: demoName, branches: demoBranches, email: demoContactEmail, phone: demoContactPhone, requestType: "Live Demo" });
  };

  const submitProposal = () => {
    submitData("FOZZIL MEDOS - Proposal Request", { plan, hardwareIncluded: includeHardware ? "Yes" : "No", requirements: requirements || "None", email: proposalEmail, phone: proposalPhone, requestType: "Commercial Proposal" });
  };

  // Features mapped to icons
  const features = [
    { title: "Core Billing & Inventory", desc: "Seamless billing operations with robust inventory control." },
    { title: "GST-ready Invoicing", desc: "Fully compliant GST tax invoicing." },
    { title: "Basic Sales Reports", desc: "Insightful daily sales and performance reports." },
    { title: "Admin OTP Approvals", desc: "Secure sensitive actions with Admin verification." },
    { title: "Multiple Staff Logins", desc: "Manage access with role-based staff accounts." },
    { title: "Email Support", desc: "Dedicated support team via email." },
    { title: "WhatsApp Billing", desc: "Send invoices directly to customers via WhatsApp." },
    { title: "Bulk Import/Export", desc: "Easy data migration via Excel/CSV." },
    { title: "Advanced Tracking", desc: "Real-time expiry and batch tracking." },
    { title: "Priority Support", desc: "Fast-track resolution for premium clients." },
    { title: "ZYPHER AI Assistant", desc: "Next-gen AI for predictive analytics and management." },
    { title: "Automated Workflows", desc: "Smart automation for repetitive tasks." },
  ];

  return (
    <CinematicProductLayout>
      {/* 1. HERO SECTION */}
      <section className="relative z-0 overflow-hidden">
          <CinematicHero
            bgImage={heroBgUrl}
          >
             {/* Dynamic Ambient Blur */}
             <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent z-[5]" />
             
             <div className="relative z-20 w-full h-full flex flex-col justify-end p-6 md:p-20 lg:p-32 max-w-[1800px] mx-auto">
                 <div className="flex flex-col lg:flex-row items-end justify-between gap-12 w-full">
                     {/* Text Content */}
                     <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="flex-1 space-y-8"
                     >
                        <div className="flex items-center gap-6">
                            <img src={logoUrl} alt="MEDOS" className="h-14 md:h-18 object-contain" />
                            <div className="h-10 w-px bg-white/20" />
                            <span className="text-zinc-500 uppercase tracking-[0.3em] text-xs font-bold font-mono">Hospital Intelligence</span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-medium text-white tracking-tighter leading-[0.9]">
                            The standard for <br />
                            <span className="text-zinc-400">digital healthcare.</span>
                        </h1>

                        <p className="text-xl md:text-2xl text-zinc-500 max-w-xl font-light leading-relaxed">
                            The world's 1st AI Hospital Pharmacy Management Operating System. 
                            Designed to elevate precision, efficiency, and care in every clinical environment.
                        </p>
                     </motion.div>

                     {/* Premium Glass CTA */}
                     <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="w-full lg:w-auto"
                     >
                        <div className="bg-white/[0.03] backdrop-blur-3xl border border-white/10 p-2 rounded-full flex flex-col md:flex-row items-center gap-2">
                            <div className="px-6 py-2 text-center md:text-left">
                                <span className="block text-[10px] text-blue-500 uppercase tracking-widest font-bold mb-1">Medos Cloud</span>
                                <span className="block text-base text-white font-medium leading-none">Global Deployment</span>
                            </div>
                            <button 
                                onClick={() => { setMode('proposal'); setStep(0); setTimeout(scrollToForms, 100); }}
                                className="w-full md:w-auto bg-white text-black px-8 py-3 rounded-full font-semibold text-base hover:bg-zinc-200 transition-all duration-300 shadow-[0_10px_25px_rgba(255,255,255,0.1)] hover:scale-[1.02] active:scale-95"
                            >
                                Get Started
                            </button>
                        </div>
                     </motion.div>
                 </div>
             </div>
          </CinematicHero>
      </section>


      {/* 3. PHILOSOPHY SECTION */}
      <section className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-black to-black" />
          <div className="max-w-6xl text-center relative z-10">
              <CinematicText className="text-3xl md:text-7xl font-medium text-white leading-tight px-4">
                  "The most advanced technology <br className="hidden md:block" /> is the one you don't even notice."
              </CinematicText>
          </div>
      </section>

      {/* 4. KEY CAPABILITIES - CARD STACK */}
      <section className="bg-black py-24">
          <div className="text-center mb-16 px-6">
               <h2 className="text-sm font-bold text-blue-500 tracking-widest uppercase mb-4">Core Ecosystem</h2>
               <h3 className="text-4xl md:text-6xl font-medium text-white">Clinical Operations, Evolved.</h3>
          </div>
          <CinematicCardStack items={[
              {
                  title: "Smart Pharmacy",
                  description: "Real-time inventory mapping with predictive stock logic. Never run out of life-saving medicine again.",
                  image: heroBgUrl
              },
              {
                  title: "Audit Defense",
                  description: "Automated multi-branch audit protection. Every transaction, every bill, perfectly compliant.",
                  image: sampleUrl
              },
              {
                  title: "Zypher Integration",
                  description: "Predictive analytics for hospital management. Foresee patient flow and operational bottlenecks.",
                  image: heroBgUrl
              }
          ]} />
      </section>

      {/* 3. INTERACTIVE FORMS (Sticky Stack) */}
      <section ref={formsRef}>
        <StackingSection index={10} className="bg-black">
        <div className="h-full w-full flex items-center justify-center p-6">
            <div className={`transition-all duration-700 w-full max-w-6xl grid gap-8 ${mode ? 'grid-cols-1 max-w-2xl' : 'grid-cols-1 md:grid-cols-2'}`}>
                
                {mode !== 'proposal' && (
                    <SpotlightCard className={`p-6 md:p-10 flex flex-col justify-between min-h-[500px] border-blue-500/30 ${mode === 'demo' ? 'ring-2 ring-blue-500 bg-neutral-900' : 'hover:bg-neutral-800'}`}>
                         {!mode ? (
                            <>
                                <div>
                                    <div className="w-16 h-16 bg-blue-900/40 rounded-[32px] flex items-center justify-center mb-8 border border-blue-500/30">
                                        <Zap className="w-8 h-8 text-blue-400" />
                                    </div>
                                    <h3 className="text-4xl font-semibold text-white mb-4">Live Demo</h3>
                                    <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                        Witness Medos operating live. See how Zypher AI handles inventory and billing in seconds.
                                    </p>
                                </div>
                                <button onClick={() => { setMode('demo'); setStep(0); }} className="w-full py-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-medium transition-all">
                                    Start Demo Flow
                                </button>
                            </>
                         ) : (
                             /* Demo Form Content (Simplified for brevity but functional) */
                             renderForm(step, 'demo', {demoName, setDemoName, demoBranches, setDemoBranches, demoContactEmail, setDemoContactEmail, demoContactPhone, setDemoContactPhone}, resetFlow, prevStep, nextStep, submitDemo, isSubmitting, submitStatus)
                         )}
                    </SpotlightCard>
                )}

                {mode !== 'demo' && (
                    <SpotlightCard className={`p-6 md:p-10 flex flex-col justify-between min-h-[500px] border-white/10 ${mode === 'proposal' ? 'ring-2 ring-white bg-neutral-900' : 'hover:bg-neutral-800'}`}>
                         {!mode ? (
                            <>
                                <div>
                                    <div className="w-16 h-16 bg-white/10 rounded-[32px] flex items-center justify-center mb-8 border border-white/20">
                                        <FileCheck2 className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-4xl font-semibold text-white mb-4">Proposal</h3>
                                    <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                        Get a tailored commercial proposal designed for your specific scale and HW requirements.
                                    </p>
                                </div>
                                <button onClick={() => { setMode('proposal'); setStep(0); }} className="w-full py-4 rounded-full border border-white/20 text-white hover:bg-white hover:text-black font-medium transition-all">
                                    Request Proposal
                                </button>
                            </>
                         ) : (
                             /* Proposal Form Content */
                             renderForm(step, 'proposal', {plan, setPlan, includeHardware, setIncludeHardware, requirements, setRequirements, proposalEmail, setProposalEmail, proposalPhone, setProposalPhone}, resetFlow, prevStep, nextStep, submitProposal, isSubmitting, submitStatus)
                         )}
                    </SpotlightCard>
                )}
            </div>
        </div>
      </StackingSection>
      </section>

      {/* 4. FEATURES (Staggered Animation) */}
      <MasonryGrid items={features} />

    </CinematicProductLayout>
  );
};

// Helper to render form steps (Extracted to keep main component clean)
const renderForm = (step, type, state, resetFlow, prevStep, nextStep, submitAction, isSubmitting, submitStatus) => {
    if (submitStatus === 'success') return (
        <div className="text-center h-full flex flex-col items-center justify-center animate-fadeIn">
            <CheckCircle2 className="w-20 h-20 text-green-500 mb-6" />
            <h3 className="text-3xl font-semibold text-white mb-2">Success!</h3>
            <p className="text-gray-400 mb-8">We have received your request.</p>
            <button onClick={resetFlow} className="px-8 py-3 bg-white/10 rounded-full text-white hover:bg-white/20">Close</button>
        </div>
    );

    return (
        <div className="h-full flex flex-col animate-fadeIn">
             <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
                <h4 className="text-xl font-semibold text-white">{type === 'demo' ? 'Book Demo' : 'Get Proposal'}</h4>
                <button onClick={resetFlow} className="text-xs text-gray-500 hover:text-white uppercase">Cancel</button>
            </div>
            
            <div className="flex-grow space-y-6">
                {/* Step Content Logic */}
                {step === 0 && type === 'demo' && (
                     <div className="space-y-4">
                        <label className="text-sm text-blue-400 font-semibold uppercase">Organization Name</label>
                        <input value={state.demoName} onChange={e => state.setDemoName(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none" placeholder="e.g. Apollo Pharmacy" />
                     </div>
                )}
                {step === 1 && type === 'demo' && (
                     <div className="space-y-4">
                        <label className="text-sm text-blue-400 font-semibold uppercase">Scale</label>
                        {['1-2 Branches', '3-10 Branches', '10+ Chain'].map(opt => (
                            <div key={opt} onClick={() => state.setDemoBranches(opt)} className={`p-4 rounded-xl border cursor-pointer ${state.demoBranches === opt ? 'bg-blue-600 border-blue-500 text-white' : 'bg-black/50 border-white/10 text-gray-400'}`}>{opt}</div>
                        ))}
                     </div>
                )}
                
                {/* Proposal Steps */}
                {step === 0 && type === 'proposal' && (
                     <div className="space-y-4">
                        <label className="text-sm text-blue-400 font-semibold uppercase">Plan</label>
                        {['Yearly Plan', 'Lifetime Deal', 'Enterprise'].map(opt => (
                            <div key={opt} onClick={() => state.setPlan(opt)} className={`p-4 rounded-xl border cursor-pointer ${state.plan === opt ? 'bg-blue-600 border-blue-500 text-white' : 'bg-black/50 border-white/10 text-gray-400'}`}>{opt}</div>
                        ))}
                     </div>
                )}
                 {step === 1 && type === 'proposal' && (
                     <div className="space-y-4">
                        <label className="flex items-center gap-4 p-4 border border-white/10 rounded-xl cursor-pointer">
                            <input type="checkbox" checked={state.includeHardware} onChange={e => state.setIncludeHardware(e.target.checked)} className="accent-blue-500 w-5 h-5"/>
                            <span className="text-white">Include Hardware Bundle</span>
                        </label>
                        <textarea value={state.requirements} onChange={e => state.setRequirements(e.target.value)} className="w-full h-32 bg-black/50 border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none" placeholder="Custom requirements..." />
                     </div>
                )}

                {/* Common Contact Step */}
                {step === 2 && (
                    <div className="space-y-4">
                        <input value={type === 'demo' ? state.demoContactEmail : state.proposalEmail} onChange={e => type === 'demo' ? state.setDemoContactEmail(e.target.value) : state.setProposalEmail(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none" placeholder="Email Address" />
                        <input value={type === 'demo' ? state.demoContactPhone : state.proposalPhone} onChange={e => type === 'demo' ? state.setDemoContactPhone(e.target.value) : state.setProposalPhone(e.target.value)} className="w-full bg-black/50 border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none" placeholder="Phone Number" />
                    </div>
                )}
            </div>

            <div className="flex justify-between pt-6 mt-6 border-t border-white/10">
                <button onClick={prevStep} disabled={step === 0} className="px-6 py-2 rounded-full text-gray-500 hover:text-white disabled:opacity-30">Back</button>
                {step < 2 ? (
                    <button onClick={nextStep} className="px-8 py-2 rounded-full bg-white text-black font-medium hover:bg-gray-200">Next</button>
                ) : (
                    <button onClick={submitAction} disabled={isSubmitting} className="px-8 py-2 rounded-full bg-green-500 text-white font-medium hover:bg-green-400">{isSubmitting ? 'Sending...' : 'Submit'}</button>
                )}
            </div>
        </div>
    );
}

export default MEDOS;